import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Editor } from '@tiptap/react';
import { Node as ProseMirrorNode, Mark } from '@tiptap/pm/model';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { highlightComments, scrollToComment as scrollToCommentInEditor, sortCommentsByDocumentOrder, sortCommentsByResolutionOrder } from '../utils/editor-utils';
import { getCommentLocationLostToastMessage } from '../utils/comment-location-feedback';
import { focusCommentNotificationTarget } from '../utils/focus-comment-notification-target';
import { CommentContent } from './CommentContent';
import { useDeleteComment } from '../hooks/use-delete-comment';
import { useUnresolveComment } from '../hooks/use-unresolve-comment';
import { useToast } from '../contexts/ToastContext';
import { API_BASE } from '../config';
import type { MentionableEntity } from './RichTextField';

type Comment = {
  id: string;
  documentId: string;
  userId: string;
  userEmail?: string;
  content: string;
  fromPos: number;
  toPos: number;
  selectedText: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  replies?: Array<{
    id: string;
    userId: string;
    userEmail?: string;
    createdAt: string;
    comment: string;
  }>;
};

type CommentsProps = {
  editor: Editor | null;
  documentId: string | undefined;
  isReadOnly?: boolean;
  isVisible?: boolean;
  refreshTrigger?: number;
  isFocusMode?: boolean;
  comments?: Comment[];
  onLoadComments?: () => Promise<void>;
  documentOwnerId?: string | null;
  hideHeading?: boolean;
  commentThreadList?: 'active' | 'resolved';
  mentionableEntities?: MentionableEntity[];
};

export const Comments: React.FC<CommentsProps> = ({
  editor,
  documentId,
  isReadOnly = false,
  isVisible = false,
  refreshTrigger = 0,
  isFocusMode = false,
  comments = [],
  onLoadComments,
  documentOwnerId,
  hideHeading = false,
  commentThreadList = 'active',
  mentionableEntities = [],
}) => {
  const { token } = useAuth();
  const { t } = useLocale();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const listRef = useRef<HTMLDivElement | null>(null);
  const focusedNotificationCommentIdRef = useRef<string | null>(null);
  const [hoveredCommentId, setHoveredCommentId] = useState<string | null>(null);
  const [commentTooltipPosition, setCommentTooltipPosition] = useState<{ top: number; left: number } | null>(null);

  const { deleteComment } = useDeleteComment({
    onCommentsUpdated: onLoadComments,
  });

  const { unresolveComment } = useUnresolveComment({
    onCommentsUpdated: onLoadComments,
  });

  const handleAddReply = async (commentId: string, content: string): Promise<void> => {
    if (!token) {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/comments/${commentId}/replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content }),
      });
      if (!response.ok) {
        console.error('Failed to add reply');
        return;
      }
      if (onLoadComments) {
        await onLoadComments();
      }
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  const handleDeleteReply = async (commentId: string, replyId: string): Promise<void> => {
    if (!token) {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/comments/${commentId}/replies/${replyId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        console.error('Failed to delete reply');
        return;
      }
      if (onLoadComments) {
        await onLoadComments();
      }
    } catch (error) {
      console.error('Error deleting reply:', error);
    }
  };

  const clearCommentHighlights = (): void => {
    if (!editor) return;
    const { state } = editor;
    const markType = state.schema.marks.highlightColor;
    if (!markType) return;

    const tr = state.tr;
    let hasChanges = false;

    const rangesToClear: Array<{ from: number; to: number }> = [];

    state.doc.descendants((node: ProseMirrorNode, pos: number) => {
      if (node.isInline && node.marks) {
        node.marks.forEach((mark: Mark) => {
          if (mark.type.name === 'highlightColor' && mark.attrs.commentId) {
            rangesToClear.push({ from: pos, to: pos + (node.textContent?.length || node.nodeSize) });
            hasChanges = true;
          }
        });
      }
    });

    rangesToClear.forEach(({ from, to }) => {
      tr.removeMark(from, to, markType);
    });

    if (hasChanges) {
      editor.view.dispatch(tr);
    }
  };

  const handleHighlightComments = useCallback(
    (commentsList: Comment[]): void => {
      const activeComments = commentsList.filter((comment) => !comment.deletedAt);
      highlightComments(editor, activeComments, {
        skipIfReadOnly: true,
        skipIfFocusMode: true,
        isReadOnly,
        isFocusMode,
      });
    },
    [editor, isReadOnly, isFocusMode]
  );

  const scrollToComment = (comment: Comment): void => {
    const result = scrollToCommentInEditor(editor, comment);
    const message = getCommentLocationLostToastMessage(result, t);
    if (message) {
      showToast(message);
    }
  };

  useEffect(() => {
    if (refreshTrigger > 0 && onLoadComments) {
      onLoadComments();
    }
  }, [refreshTrigger, onLoadComments]);

  const notificationCommentId = searchParams.get('commentId');
  const notificationReplyId = searchParams.get('replyId');
  const notificationFocusReply = searchParams.get('focusReply') === '1';

  const hasNotificationComment = Boolean(
    notificationCommentId && comments.some((comment) => comment.id === notificationCommentId)
  );

  useEffect(() => {
    if (!notificationCommentId) {
      focusedNotificationCommentIdRef.current = null;
    }
  }, [notificationCommentId]);

  useEffect(() => {
    if (!isVisible || !notificationCommentId || !hasNotificationComment) {
      return;
    }
    if (focusedNotificationCommentIdRef.current === notificationCommentId) {
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let retryTimeoutId: number | undefined;

    const clearNotificationParams = (): void => {
      setSearchParams((prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.delete('commentId');
        nextParams.delete('replyId');
        nextParams.delete('focusReply');
        return nextParams;
      }, { replace: true });
    };

    const tryFocus = (): void => {
      if (cancelled) {
        return;
      }
      const root = listRef.current;
      if (!root) {
        if (attempts < 40) {
          attempts += 1;
          retryTimeoutId = window.setTimeout(tryFocus, 100);
        }
        return;
      }
      attempts += 1;
      const focused = focusCommentNotificationTarget({
        commentId: notificationCommentId,
        replyId: notificationReplyId,
        focusReply: notificationFocusReply,
        root,
      });
      if (focused) {
        focusedNotificationCommentIdRef.current = notificationCommentId;
        clearNotificationParams();
        return;
      }
      if (attempts < 40) {
        retryTimeoutId = window.setTimeout(tryFocus, 100);
      }
    };

    const timeoutId = window.setTimeout(tryFocus, 50);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      if (retryTimeoutId !== undefined) {
        window.clearTimeout(retryTimeoutId);
      }
    };
  }, [
    isVisible,
    hasNotificationComment,
    notificationCommentId,
    notificationReplyId,
    notificationFocusReply,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!editor || isReadOnly) return;

    if (isFocusMode) {
      clearCommentHighlights();
    }
  }, [isFocusMode, editor, isReadOnly]);

  useEffect(() => {
    if (!editor || isReadOnly || isFocusMode) return;

    const timeoutId = setTimeout(() => {
      handleHighlightComments(comments);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [editor, comments, isReadOnly, isFocusMode, handleHighlightComments]);

  useEffect(() => {
    if (!editor || comments.length === 0 || !isVisible) {
      setHoveredCommentId(null);
      setCommentTooltipPosition(null);
      return;
    }

    const editorElement = editor.view.dom;
    if (!editorElement) return;

    const findParentCommentMark = (element: HTMLElement | null): HTMLElement | null => {
      let current = element;
      while (current && current !== editorElement) {
        if (current.tagName === 'MARK' && current.hasAttribute('data-comment-id')) {
          return current;
        }
        current = current.parentElement;
      }
      return null;
    };

    const updateTooltipPosition = (mark: HTMLElement): void => {
      const rect = mark.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      let top = rect.top - 10;
      let left = rect.left + rect.width / 2;

      if (top < 100) {
        top = rect.bottom + 10;
      }

      if (left < 150) {
        left = 150;
      } else if (left > viewportWidth - 150) {
        left = viewportWidth - 150;
      }

      setCommentTooltipPosition({ top, left });
    };

    const handleMouseOver = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      const mark = findParentCommentMark(target);
      if (mark) {
        const commentId = mark.getAttribute('data-comment-id');
        if (commentId) {
          setHoveredCommentId(commentId);
          updateTooltipPosition(mark);
        }
      }
    };

    const handleMouseOut = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      const mark = findParentCommentMark(target);
      if (mark) {
        const relatedMark = findParentCommentMark(relatedTarget);
        if (!relatedMark || relatedMark !== mark) {
          setHoveredCommentId(null);
          setCommentTooltipPosition(null);
        }
      }
    };

    const handleMouseMove = (e: MouseEvent): void => {
      if (hoveredCommentId) {
        const target = e.target as HTMLElement;
        const mark = findParentCommentMark(target);
        if (mark) {
          updateTooltipPosition(mark);
        }
      }
    };

    editorElement.addEventListener('mouseover', handleMouseOver);
    editorElement.addEventListener('mouseout', handleMouseOut);
    editorElement.addEventListener('mousemove', handleMouseMove);

    return () => {
      editorElement.removeEventListener('mouseover', handleMouseOver);
      editorElement.removeEventListener('mouseout', handleMouseOut);
      editorElement.removeEventListener('mousemove', handleMouseMove);
    };
  }, [editor, comments, hoveredCommentId, isVisible]);

  useEffect(() => {
    if (!editor || comments.length === 0 || !isVisible) return;

    const editorElement = editor.view.dom;
    if (!editorElement) return;

    if (!hoveredCommentId) {
      const marks = editorElement.querySelectorAll('mark[data-comment-id]');
      marks.forEach((mark: Element) => {
        const element = mark as HTMLElement;
        const originalColor = element.getAttribute('data-color');
        if (originalColor) {
          element.style.backgroundColor = originalColor;
        }
      });
      return;
    }

    const marks = editorElement.querySelectorAll('mark[data-comment-id]');
    marks.forEach((mark: Element) => {
      const element = mark as HTMLElement;
      const commentIdAttr = element.getAttribute('data-comment-id');
      const originalColor = element.getAttribute('data-color');

      if (commentIdAttr === hoveredCommentId && originalColor) {
        const baseColor = originalColor;
        const hoverColor = baseColor.replace('0.3', '0.5');
        element.style.backgroundColor = hoverColor;
      } else if (originalColor) {
        element.style.backgroundColor = originalColor;
      }
    });
  }, [editor, comments, hoveredCommentId, isVisible]);

  if (!isVisible) {
    return null;
  }

  const orderedComments =
    commentThreadList === 'resolved'
      ? sortCommentsByResolutionOrder(comments)
      : sortCommentsByDocumentOrder(editor, comments);

  return (
    <div className="flex flex-col h-full" ref={listRef}>
      {!hideHeading && (
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--foreground)' }}>{t('comments')}</h3>
      )}
      {orderedComments.length === 0 ? (
        <div style={{ color: 'var(--muted-foreground)', fontSize: '0.875rem' }}>
          {commentThreadList === 'resolved' ? t('no_resolved_comments') : t('no_comments')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {orderedComments.map((comment) => (
            <div
              key={comment.id}
              data-comment-thread-id={comment.id}
              onClick={() => scrollToComment(comment)}
              style={{
                padding: '0.75rem',
                borderRadius: '0.5rem',
                backgroundColor: 'var(--card)',
                color: 'var(--card-foreground)',
                border: '1px solid var(--border)',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--muted)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--card)';
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <CommentContent
                  comment={comment}
                  onDeleteComment={async (commentId) => {
                    await deleteComment(commentId);
                  }}
                  onUnresolveComment={
                    commentThreadList === 'resolved'
                      ? async (commentId) => {
                          await unresolveComment(commentId);
                        }
                      : undefined
                  }
                  showDeleteButton={true}
                  documentOwnerId={documentOwnerId}
                  onAddReply={commentThreadList === 'active' ? handleAddReply : undefined}
                  onDeleteReply={commentThreadList === 'active' ? handleDeleteReply : undefined}
                  commentThreadList={commentThreadList}
                  mentionableEntities={mentionableEntities}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};






